import { Button } from "@convergence/components";
import { useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useConnectedAppsQuery,
  useRevokeConnectedAppMutation,
} from "../api/connectedApps";
import { useAuthContext } from "../components/AuthProvider";
import { Header } from "./LoginOtp";

export function ApiTokens() {
  const { user, isLoading } = useAuthContext();

  if (isLoading) return <div role="status">Loading...</div>;
  if (!user) {
    return <Navigate to="/login?redirect=%2Fmcp" replace />;
  }

  return <McpSetup />;
}

function McpSetup() {
  const mcpUrl = `${import.meta.env.VITE_API_ENDPOINT.replace(/\/$/, "")}/mcp`;
  const serverUrl = <code className="break-all">{mcpUrl}</code>;

  return (
    <div className="min-h-screen bg-white text-black font-body">
      <Header />
      <main className="mx-auto max-w-2xl p-4 sm:p-8">
        <section className="flex flex-col gap-6 border border-black p-6">
          <h1 className="text-4xl">Connect to MCP</h1>
          <p>
            Connect Claude, ChatGPT, or another MCP client to Tinker. Clients
            sign in with OAuth: you sign in to Tinker and approve access when
            the client asks.
          </p>
          <section className="flex flex-col gap-3 border-t border-black pt-6">
            <h2 className="text-2xl">Claude</h2>
            <p>
              Connectors you add on claude.ai are also available in Claude
              Desktop and Claude mobile.
            </p>
            <ol className="list-decimal pl-5 space-y-3">
              <li>
                In Claude, open <strong>Settings → Connectors</strong> and select
                {" "}<strong>Add custom connector</strong>. On Team and Enterprise
                plans, an owner adds the connector in organization settings first.
              </li>
              <li>
                Name it Tinker, enter {serverUrl} as the remote MCP server URL,
                and select <strong>Add</strong>.
              </li>
              <li>Select <strong>Connect</strong>, sign in to Tinker and allow access.</li>
              <li>Enable Tinker in the chat’s tools menu.</li>
            </ol>
            <p>
              Try: “Use Tinker to set the execution context to my box named
              &lt;box-name&gt;, then show the current execution context.”
              Starting a stopped box can take several minutes.
            </p>
            <p>
              To remove access, revoke Claude under Connected apps below, then
              remove the connector in Claude’s Settings → Connectors.
            </p>
            <a className="underline" href="https://support.claude.com/en/articles/11175166-getting-started-with-custom-connectors-using-remote-mcp" target="_blank" rel="noreferrer">
              Claude custom connector setup and requirements
            </a>
          </section>
          <section className="flex flex-col gap-3 border-t border-black pt-6">
            <h2 className="text-2xl">ChatGPT</h2>
            <ol className="list-decimal pl-5 space-y-3">
              <li>Enable Developer mode in ChatGPT’s Settings → Security and login (if available for your account or workspace).</li>
              <li>
                Open ChatGPT’s apps page, use the plus button to create a
                developer-mode app, and enter {serverUrl} as the MCP server URL.
              </li>
              <li>Select OAuth, sign in to Tinker and allow access, then select the app from the conversation’s Developer mode tools.</li>
            </ol>
            <p>To remove access, revoke ChatGPT under Connected apps below.</p>
            <a className="underline" href="https://developers.openai.com/api/docs/guides/developer-mode" target="_blank" rel="noreferrer">
              ChatGPT developer mode setup and requirements
            </a>
          </section>
          <section className="flex flex-col gap-3 border-t border-black pt-6">
            <h2 className="text-2xl">Other MCP clients</h2>
            <p>
              Add a remote server using Streamable HTTP with the URL {serverUrl}
              {" "}and choose OAuth when the client asks how to authenticate.
            </p>
          </section>
          <ConnectedApps />
        </section>
      </main>
    </div>
  );
}

const SCOPE_LABELS: Record<string, string> = {
  mcp: "Use Tinker tools",
  offline_access: "Stay connected",
};

type AppToRevoke = { clientId: string; name: string };

function ConnectedApps() {
  const query = useConnectedAppsQuery();
  const revoke = useRevokeConnectedAppMutation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [appToRevoke, setAppToRevoke] = useState<AppToRevoke>();

  function confirmRevoke(app: AppToRevoke) {
    setAppToRevoke(app);
    dialogRef.current?.showModal();
  }

  function revokeApp() {
    if (!appToRevoke) return;
    const { clientId, name } = appToRevoke;
    revoke.mutate(clientId, {
      onSuccess: () => {
        dialogRef.current?.close();
        toast.success(`${name} can no longer access Tinker`);
      },
      onError: () => toast.error(`Unable to revoke ${name}. Please try again.`),
    });
  }

  return (
    <section className="flex flex-col gap-3 border-t border-black pt-6">
      <h2 className="text-2xl">Connected apps</h2>
      <p>
        Apps you’ve allowed to use Tinker through OAuth. Revoking an app signs it
        out right away; you can connect it again later.
      </p>
      {query.isPending && <p role="status">Loading...</p>}
      {query.isError && (
        <p role="alert" className="text-red-700">
          Unable to load connected apps. Please refresh the page.
        </p>
      )}
      {query.data?.apps.length === 0 && <p>No apps are connected.</p>}
      {query.data && query.data.apps.length > 0 && (
        <ul className="flex flex-col gap-3">
          {query.data.apps.map((app) => {
            const name = app.name || "Unnamed app";
            const revoking = revoke.isPending && revoke.variables === app.clientId;
            const scopes = app.scopes
              .map((scope) => SCOPE_LABELS[scope])
              .filter(Boolean)
              .join(", ");
            return (
              <li
                key={app.clientId}
                className="flex flex-wrap items-center justify-between gap-3 border border-black p-3"
              >
                <div className="min-w-0">
                  <p className="break-words">{name}</p>
                  <p className="text-sm">
                    Connected <time dateTime={app.connectedAt}>
                      {new Date(app.connectedAt).toLocaleDateString()}
                    </time>
                    {scopes && ` · ${scopes}`}
                  </p>
                </div>
                <Button
                  disabled={revoke.isPending}
                  onClick={() => confirmRevoke({ clientId: app.clientId, name })}
                >
                  {revoking ? "Revoking..." : "Revoke"}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <dialog
        ref={dialogRef}
        aria-labelledby="revoke-app-title"
        className="w-[min(28rem,calc(100%-2rem))] border border-black bg-white p-6 text-black backdrop:bg-black/50"
        onCancel={(event) => {
          if (revoke.isPending) event.preventDefault();
        }}
        onClose={() => setAppToRevoke(undefined)}
      >
        <div className="flex flex-col gap-4">
          <h3 id="revoke-app-title" className="text-xl break-words">
            Revoke {appToRevoke?.name}?
          </h3>
          <p>
            {appToRevoke?.name} will lose access to Tinker right away. You can
            connect it again later.
          </p>
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="gray"
              disabled={revoke.isPending}
              onClick={() => dialogRef.current?.close()}
            >
              Cancel
            </Button>
            <Button variant="red" disabled={revoke.isPending} onClick={revokeApp}>
              {revoke.isPending ? "Revoking..." : "Revoke"}
            </Button>
          </div>
        </div>
      </dialog>
    </section>
  );
}
